import Sentry from '@/sentry';
import { CUSTOM_BRANDING } from './Branding/constants';
import { handleForbidden } from '@/helpers';

const minimumLoadingTime = 400;

const DesktopPreviewer = () => {
    return {
        pageNum: 1,
        isLoading: true,
        preloadedPages: [],
        previewDimensions: { naturalHeight: 0, naturalWidth: 0, clientHeight: 0, clientWidth: 0 },
        logoVersion: Date.now(),

        init() {
            this.$watch('unsupportedBrowser', (value) => value && handleUnsupportedBrowser(this));

            this.$watch('previews', async (value) => {
                value && Object.keys(value).length > 0 && await this.handleDesktopPreviewer();
            });

            this.$watch('userLogo', (newLogo) => {
                if (newLogo) {
                    this.logoVersion = Date.now();
                }
            });
        },

        async handleDesktopPreviewer(){
            this.pageNum = 1;
            this.isLoading = true;

            let loaded = false;
            let timeElapsed = false;

            setTimeout(() => {
                timeElapsed = true;

                if(loaded){
                    this.isLoading = false;
                }
            }, minimumLoadingTime);

            this.renderPage(this.pageNum)
                .catch((error) => this.isDesktop && errorHandler(error, this))
                .finally(() => {
                    loaded = true;

                    if(timeElapsed){
                        this.isLoading = false;
                    }
                });
        },

        async renderPage(num){
            this.$refs.nextPage.classList.add('cursor-wait');
            this.$refs.prevPage.classList.add('cursor-wait');

            this.$root.classList.add('cursor-wait');

            return new Promise((resolve, reject) => {
                this.$refs.thePdf.onload = () => {

                    const unresizedWidth = this.$refs.thePdf.width;

                    if(unresizedWidth >= this.$root.clientWidth){
                        this.$refs.thePdf.style.maxWidth = 'calc(100% - 40px)';
                        this.$refs.thePdf.classList.add('mx-auto');
                    }

                    resolve();

                    this.$nextTick(() => this.updatePreviewDimensions());
                    this.preloadNextPage();
                };
                this.$refs.thePdf.onerror = reject;
                this.$refs.thePdf.src = this.previews[num];

                this.handleDisplayControls(num);

                this.$refs.currentPage.textContent = num;
                this.$refs.totalPage.textContent = this.pages;
            })
            .finally(() => {
                this.$refs.nextPage.classList.remove('cursor-wait');
                this.$refs.prevPage.classList.remove('cursor-wait');

                this.$root.classList.remove('cursor-wait');
            });
        },

        preloadNextPage(){
            let nextPageNumber = this.pageNum + 1;

            if (nextPageNumber <= this.pages && typeof this.preloadedPages[nextPageNumber] === 'undefined') {
                const image = new Image();
                const previewImage = this.previews[nextPageNumber];
                image.src = previewImage;
                this.preloadedPages[nextPageNumber] = previewImage;
            }
        },

        async onPrevPage(){
            if (this.pageNum <= 1) {
                return;
            }

            this.pageNum--;
            await this.renderPage(this.pageNum);
        },

        async onNextPage(){
            if (this.pageNum >= this.pages) {
                return;
            }

            this.pageNum++;
            await this.renderPage(this.pageNum);
        },

        handleDisplayControls(page){
            this.$refs.nextPage.classList.remove('invisible');
            this.$refs.prevPage.classList.remove('invisible');
            this.$refs.prevPage.disabled = false;
            this.$refs.nextPage.disabled = false;

            if(page === 1 && this.pages === 1){
                this.$refs.prevPage.classList.add('invisible');
                this.$refs.nextPage.classList.add('invisible');
                return;
            }
            if(page === 1){
                this.$refs.prevPage.disabled = true;
                return;
            }
            if(this.pages === 1 || page === this.pages){
                this.$refs.nextPage.disabled = true;
                return;
            }
        },

        updatePreviewDimensions(){
            const pdf = this.$refs.thePdf;
            const pdfRect = pdf.getBoundingClientRect();
            const containerRect = pdf.parentElement.getBoundingClientRect();

            this.previewDimensions = {
                naturalHeight: pdf.naturalHeight,
                naturalWidth: pdf.naturalWidth,
                clientHeight: pdf.clientHeight,
                clientWidth: pdf.clientWidth,

                offsetLeft: pdfRect.left - containerRect.left,
                offsetTop: pdfRect.top - containerRect.top,
            };
        },
    }
};

const MobilePreviewer = () => {
    return {
        isLoading: true,
        hasLoaded: false,
        previewDimensions: [],
        logoVersion: Date.now(),

        init() {
            this.$watch('unsupportedBrowser', (value) => value && handleUnsupportedBrowser(this));

            this.$watch('previews', async (value) => {
                !this.isDesktop && value && Object.keys(value).length > 0 && await this.handleMobilePreviewer();

                if(this.hasLoaded && this.isDesktop){
                    this.hasLoaded = false;
                }
            });

            this.$watch('userLogo', (newLogo) => {
                if (newLogo) {
                    this.logoVersion = Date.now();
                }
            });

            window.addEventListener(
                'resize',
                async () => this.pages > 0 &&!this.isDesktop && !this.hasLoaded && await this.handleMobilePreviewer()
            );
        },

        async handleMobilePreviewer()  {
            this.isLoading = true;

            let loaded = false;
            let timeElapsed = false;

            setTimeout(() => {
                timeElapsed = true;

                if(loaded){
                    this.isLoading = false;
                }
            }, minimumLoadingTime);

            this.clearOldImages();

            this.renderAllPages()
                .catch((error) => !this.isDesktop && errorHandler(error, this))
                .finally(() => {
                    loaded = true;

                    if(timeElapsed){
                        this.isLoading = false;
                    }

                    this.hasLoaded = true;

                    this.handlePageCalculations();
                    this.handleHorizontalScroll();

                    this.$refs.totalPageMobile.textContent = this.pages;
                });
        },

        clearOldImages() {
            const firstImage = this.$refs.viewer.querySelector('.page-wrapper[page="1"] > img.new-pdf-mobile-preview');

            if(firstImage instanceof HTMLImageElement){
                this.$refs.viewer.style.minHeight = `${firstImage.height}px`;
            }

            this.$refs.viewer.innerHTML = '';
        },

        async renderAllPages() {
            const promises = Object.keys(this.previews)
                    .map(async (page) => new Promise(
                        (resolve, reject) => {
                            const image = new Image();
                            const wrapper = document.createElement('div');

                            image.className = `new-pdf-mobile-preview`;
                            image.src = this.previews[page];

                            wrapper.setAttribute('page', page);

                            image.onload = () => {
                                resolve();

                                this.updatePreviewDimensions(image, page);
                                this.handleLogoPlacement(wrapper, page);
                            };
                            image.onerror = reject;
                            image.setAttribute('x-on:resize.window.debounce', `updatePreviewDimensions($el, ${page})`);
                            image.setAttribute('x-intersect.once', `updatePreviewDimensions($el, ${page})`);

                            wrapper.classList.add('relative', 'page-wrapper');
                            wrapper.appendChild(image);

                            this.$refs.viewer.appendChild(wrapper);
                        })
                    );

            return Promise.all(promises);
        },

        handleLogoPlacement(parent, page){
            if (typeof this.placementLogoCoordinates[page] === 'undefined') return;

            this.placementLogoCoordinates[page].forEach(({x,y,width,height}) => {
                const logo = new Image();

                logo.setAttribute('x-bind:src', `userLogo ? userLogo + (logoVersion ? '?v=' + logoVersion : '') : ''`);
                logo.setAttribute('alt', 'Logo');
                logo.classList.add('absolute');

                logo.setAttribute(':style', `() => {
                    const dimensions = previewDimensions[${page}];

                    const scaleX = dimensions?.clientWidth / dimensions?.naturalWidth;
                    const scaleY = dimensions?.clientHeight / dimensions?.naturalHeight;

                    if (!scaleX || !scaleY || isNaN(scaleX) || isNaN(scaleY)) {
                        return { display: 'none' };
                    }

                    return {
                        left: (${x} * scaleX) + 'px',
                        top: (${y} * scaleY) + 'px',
                        width: (${width} * scaleX) + 'px',
                        height: (${height} * scaleY) + 'px'
                    };
                }`);

                logo.setAttribute('x-show', `userLogo && isOptionSelected({ customization: 'branding', option: '${CUSTOM_BRANDING.option}' })`);

                parent.appendChild(logo);
            });
        },

        handleHorizontalScroll(){
            const childNodes = this.$refs.viewer.childNodes;

            if(childNodes.length < 1){
                return;
            }

            const mappedElements = [];

            const guessActivePageByNearestOffset = (items, offset) => items.reduce((prev, curr) => Math.abs(curr?.offset - offset) < Math.abs(prev?.offset - offset) ? curr : prev);

            childNodes.forEach((element) => mappedElements.push({ element, offset: element.getBoundingClientRect().left }));

            const { element } = guessActivePageByNearestOffset(mappedElements, this.$refs.viewer.scrollLeft);

            this.$refs.currentPageMobile.textContent = element.getAttribute('page');
        },

          handlePageCalculations(){
               if(!this.$refs.viewer.firstChild){
                   return;
               }

               const pageOne = this.$refs.viewer.firstChild;
               const lastPage = this.$refs.viewer.lastChild;
               const horizontalMargin = (this.$refs.container.offsetWidth - pageOne.offsetWidth) / 2;
               const isMobile = window.innerWidth < 744;
               const hasMultiplePages = this.$refs.viewer.childNodes.length > 1;
               const contentMargin = isMobile && hasMultiplePages ? 16 : 0;

               pageOne.style.marginLeft = `${hasMultiplePages && isMobile ? contentMargin : horizontalMargin}px`;

               if(hasMultiplePages){
                   lastPage.style.marginRight = `${horizontalMargin}px`;
               }
           },

        updatePreviewDimensions(element, page){
            this.previewDimensions[page] = {
                naturalHeight: element.naturalHeight,
                naturalWidth: element.naturalWidth,
                clientHeight: element.clientHeight,
                clientWidth: element.clientWidth
            };
        },
    }
};

const errorHandler = (error, context = {}) => {
    console.error(error);

    if(error?.status === 429){
        dispatchEvent(
            new CustomEvent("notify", {
                detail: {
                    title: "Oops!",
                    text: "You've made too many requests in a short period. Please wait a few minutes before trying again.",
                    type: "warning"
                }
            })
        );

        return;
    }

    if(error?.status === 403){
        handleForbidden();

        return;
    }

    const { selected, userHasPremium, previews, worksheet, content } = context;

    // log 20% of exceptions
    Math.random() < 0.2 && Sentry.captureException(error, (scope) => {
        scope.setContext('details', {
            selected: JSON.stringify(selected),
            userHasPremium,
            previews: JSON.stringify(previews),
            worksheet: JSON.stringify(worksheet),
            online: window.navigator.onLine,
        });

        scope.setTag('content', content);
    });

    dispatchEvent(
        new CustomEvent("notify", {
            detail: {
                title: "Oops!",
                text: "An unexpected error occurred on PDF preview.",
                type: "danger",
                action: location.href,
                actionText: 'Try to update your page'
            }
        })
    );

    context.previews = [];
};

const handleUnsupportedBrowser = (context) => {
    if(context?.isLoading){
        context.isLoading = false;
    }

    if(context?.previews){
        context.previews = [];
    }
};

export { DesktopPreviewer, MobilePreviewer };
